import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { FloatLabel } from "primereact/floatlabel";
import { WeekPicker } from "./WeekPicker";
import { SmartProjectSelector } from "./SmartProjectSelector";
import { SmartEmployeeSelector } from "./SmartEmployeeSelector";
import { HourType } from "./Badges";
import { apiService, SchedulePatch } from "../services/apiService";
import { useActivitiesContext } from "../contexts/ActivitiesContext";
import { getCurrentWeek, formatWeek } from "../utils/weekUtils";

interface RangeDeleteModalProps {
  visible: boolean;
  onHide: () => void;
  onDelete: (data: {
    projectId: string;
    employeeIds: number[];
    startWeek: number;
    startYear: number;
    endWeek: number;
    endYear: number;
  }) => void;
  onSchedulePatch?: (patch: SchedulePatch) => void;

  // Pre-filled values (for when opened from a specific project/employee)
  initialData?: {
    projectId?: string;
    activityId?: number;
    employeeIds?: number[];
    startWeek?: number;
    startYear?: number;
    endWeek?: number;
    endYear?: number;
    disableProjectSelection?: boolean;
    disableEmployeeSelection?: boolean;
  };
}

export const RangeDeleteModal: React.FC<RangeDeleteModalProps> = ({
  visible,
  onHide,
  onDelete,
  onSchedulePatch,
  initialData,
}) => {
  // Get data from contexts
  const { activities } = useActivitiesContext();

  // Transform context data to match component expectations
  const projects = activities
    .filter((activity) => activity.project && activity.project.client) // Filter out activities without project data
    .map((activity) => ({
      id: activity.id.toString(),
      activityId: activity.id,
      name: activity.name,
      activity: activity.name,
      client: activity.project!.client.name,
      project: activity.project!.name,
      hourType: activity.hour_type as HourType,
      reference: activity.project!.reference,
    }));

  const [selectedProject, setSelectedProject] = useState<string>("");
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [startWeek, setStartWeek] = useState<number>(getCurrentWeek());
  const [startYear, setStartYear] = useState<number>(new Date().getFullYear());
  const [endWeek, setEndWeek] = useState<number>(getCurrentWeek());
  const [endYear, setEndYear] = useState<number>(new Date().getFullYear());
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Reset form when modal opens
  useEffect(() => {
    if (visible) {
      // Use initial data if provided, otherwise reset to defaults
      setSelectedProject(initialData?.projectId || "");
      setSelectedEmployees(initialData?.employeeIds || []);

      const currentWeek = getCurrentWeek();
      const currentYear = new Date().getFullYear();

      setStartWeek(initialData?.startWeek || currentWeek);
      setStartYear(initialData?.startYear || currentYear);
      setEndWeek(initialData?.endWeek || currentWeek);
      setEndYear(initialData?.endYear || currentYear);
      setErrors({});
    }
  }, [visible, initialData]);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedProject) {
      newErrors.project = "Please select a project";
    }

    if (selectedEmployees.length === 0) {
      newErrors.employees = "Please select at least one employee";
    }

    const startDate = new Date(startYear, 0, 1);
    startDate.setDate(startDate.getDate() + (startWeek - 1) * 7);

    const endDate = new Date(endYear, 0, 1);
    endDate.setDate(endDate.getDate() + (endWeek - 1) * 7);

    if (endDate < startDate) {
      newErrors.endWeek = "End week must be after or equal to start week";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDelete = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Get activityId from selected project or initial data
      const selectedProjectData = projects.find(
        (p) => p.id === selectedProject,
      );
      const activityId =
        initialData?.activityId ?? selectedProjectData?.activityId;

      if (activityId == null) {
        console.error("No activity ID available for range delete");
        return;
      }

      const weekFrom = formatWeek(startYear, startWeek);
      const weekTo = formatWeek(endYear, endWeek);

      const clientMutationId = crypto.randomUUID();
      const response = await apiService.deleteEventRange(
        {
          people: selectedEmployees,
          activity: activityId,
          week_from: weekFrom,
          week_to: weekTo,
        },
        clientMutationId,
      );

      if (response.success) {
        if (response.data && onSchedulePatch) {
          onSchedulePatch({
            ...response.data,
            client_mutation_id:
              response.data.client_mutation_id ?? clientMutationId,
          });
        } else if (onSchedulePatch) {
          onSchedulePatch({
            type: "schedule.patch",
            origin: "client",
            client_mutation_id: clientMutationId,
            changes: [],
          });
        }

        // Call the legacy onDelete callback for compatibility
        onDelete({
          projectId: selectedProject,
          employeeIds: selectedEmployees,
          startWeek,
          startYear,
          endWeek,
          endYear,
        });

        onHide();
      } else {
        console.error("Range delete failed:", response.message);
        // You might want to show an error message to the user here
      }
    } catch (error) {
      console.error("Error deleting range:", error);
      // You might want to show an error message to the user here
    }
  };

  return (
    <Dialog
      header="Delete Schedule Range"
      visible={visible}
      style={{ width: "550px" }}
      onHide={onHide}
      resizable={false}
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-outlined"
            onClick={onHide}
          />
          <Button
            label="Delete Range"
            icon="pi pi-trash"
            className="p-button-danger"
            onClick={handleDelete}
          />
        </div>
      }
      closable={true}
      modal={true}
    >
      <div className="grid">
        {/* Project Selection */}
        <div className="col-12">
          <SmartProjectSelector
            value={selectedProject}
            onChange={setSelectedProject}
            disabled={initialData?.disableProjectSelection}
            error={errors.project}
          />
        </div>

        {/* Employee Selection */}
        <div className="col-12">
          <SmartEmployeeSelector
            value={selectedEmployees}
            onChange={setSelectedEmployees}
            disabled={initialData?.disableEmployeeSelection}
            error={errors.employees}
          />
        </div>

        {/* Week Range Selection */}
        <div className="col-6">
          <div className="field">
            <FloatLabel>
              <WeekPicker
                value={startWeek}
                year={startYear}
                onChange={(week, selectedYear) => {
                  setStartWeek(week);
                  setStartYear(selectedYear);
                }}
              />
              <label className="block text-sm font-medium">Start Week *</label>
            </FloatLabel>
          </div>
        </div>

        <div className="col-6">
          <div className="field">
            <FloatLabel>
              <WeekPicker
                value={endWeek}
                year={endYear}
                minWeek={startWeek}
                minYear={startYear}
                onChange={(week, selectedYear) => {
                  setEndWeek(week);
                  setEndYear(selectedYear);
                }}
              />
              <label className="block text-sm font-medium">End Week *</label>
            </FloatLabel>
            {errors.endWeek && (
              <small className="p-error">{errors.endWeek}</small>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
